<template>
  <div class="draggable-item__wrapper">
    <draggable
      v-model="widgets"
      group="widgets"
      :ghost-class="`widget-ghost${isIPad ? '--ipad' : ''}`"
      :chosen-class="`widget-chosen${isIPad ? '--ipad' : ''}`"
      :drag-class="`widget-drag${isIPad ? '--ipad' : ''}`"
      :disabled="disableDrag"
      draggable=".isdraggable"
      :delay="isIPad || isTouchDevice ? 80 : 0"
      :delay-on-touch-only="isTouchDevice || isIPad"
      :animation="200"
      :class="`draggable-item ${disabledTint ? 'disabled-widget' : ''} ${dragging ? 'draggable-dragging' : ''}
       ${widgets && !widgets.length && 'draggable-item--empty'}`"
      :empty-insert-threshold="95"
      @change="$emit('save')"
      @add="onAdd"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <div
        v-for="(widget, index) in widgets"
        :key="`draggablewidget_${index}`"
        v-drag-scroll
        :class="`${ $store.state.sections.dragEnabled && widgets.length ? 'isdraggable' : ''}
        ${isIPad ? 'is-ipad' : ''}`"
      >
        <div
          v-if="debug || dragging && isIPad"
          class="widget-collapsed--ipad d-flex justify-content-between"
        >
          <div>
            <base-heading
              class="m-0"
              style="width: auto;"
            >
              {{ getTitle(widget.type) }}
            </base-heading>
          </div>
          <div class="widget-collapsed__preview d-flex justify-content-center align-items-center">
            <base-icon
              v-if="widget.type === 'text-widget'"
              :icon="`${isWidgetEmpty(widget) ? 'text-widget-empty' : 'text-widget-empty'}`"
            />
            <template v-else-if="widget.type === 'image-widget'">
              <base-icon
                v-if="isWidgetEmpty(widget)"
                icon="image-widget-empty"
              />
              <div
                v-else-if="getImageWidgetThumb(widget).isSVG"
                class="svg-preview"
                v-html="getImageWidgetThumb(widget).src"
              />
              <img
                v-else
                :src="getImageWidgetThumb(widget).src"
                :alt="`image widget ${widget.url}`"
              >
            </template>
            <template v-else-if="widget.type === 'product-widget'">
              <base-icon
                v-if="isWidgetEmpty(widget)"
                icon="product-widget-empty"
              />
              <img
                v-else
                class="widget-collapsed__preview--product"
                :src="getProductWidgetThumb(widget)"
                alt=""
              >
            </template>
            <div
              v-else
              class="d-none"
            />
          </div>
        </div>
        <base-widget
          v-show="!debug && widgetIsLoading(widget)"
          :title="getTitle(widget.type)"
          loading
          class="mt-3"
          style="user-select: none"
          :class="dragging && isIPad ? 'dragging--ipad' : ''"
        />
        <block-widget
          v-show="!debug && !widgetIsLoading(widget)"
          ref="widget_components"
          :disabled="disableDrag"
          :title="getTitle(widget.type)"
          :index="index"
          :widget="widget"
          :url="widget.url"
          :class="dragging && isIPad ? 'dragging--ipad' : ''"
          @deleteBlock="onDelete"
          @componenUpdated="componentUpdated"
          @savingchange="onSavingChange"
        />
      </div>
      <!--     <pre style="position: absolute; top: 150px; left: 0; z-index:9999">DEBUG:
    Saving: {{saving}}
    status: {{status}}</pre> -->
    </draggable>
    <div
      v-if="widgets && !widgets.length"
      class="w-100 text-center empty-widget__wrapper"
    >
      <div class="empty-widget">
        <base-heading
          level="3"
          class="mb-0 start-heading"
        >
          Start your edit
        </base-heading>
        <base-text
          class="start-body"
          light
          size="m"
        >
          Drag and drop from the toolbar on the right to start building your edit
        </base-text>
      </div>
    </div>
  </div>
</template>
<script>
import draggable from 'vuedraggable';
import blockWidget from '@/components/BlockComponent';
import whichbrowser from '@/components/mixins/whichbrowser';

export default {
  name: 'DraftEditorDraggableWidgets',
  components: {
    draggable,
    blockWidget,
  },
  mixins: [whichbrowser],
  data() {
    return {
      saving: false,
      status: '',
      disabledTint: false,
      dragging: false,
      debug: false,
    };
  },
  computed: {
    draftId() {
      return this.$route.params.id;
    },
    widgets: {
      get() {
        const editSections = this.$store.state.entries.item.sections || [];
        // console.log(`editSections.length: ${editSections.length}`);
        return editSections;
      },
      set(value) {
        // Only add widgets with valid 'type'
        return this.$store.commit('entries/updateSections', value.filter((w) => w.type.includes('-widget')));
      },
    },
    disableDrag() {
      return !this.$store.state.entries.draggable
        // this.$store.state.entries.isTextFocused ||
        || ['saving', 'loading'].includes(this.patchStatus);
    },
    patchStatus() {
      return this.$store.state.entries.status;
    },
    isTextFocused() {
      return this.$store.state.entries.isTextFocused;
    },
  },
  watch: {
    patchStatus(newval) {
      if (['saving', 'loading'].includes(newval)) {
        window.setTimeout(() => {
          this.disabledTint = ['saving', 'loading'].includes(this.patchStatus);
        }, 2000);
      } else {
        this.disabledTint = false;
      }
    },
    widgets(newVal) {
      // Always filter invalid widgets
      // eslint-disable-next-line
      newVal = newVal.filter((w) => w.type.includes('-widget'));
    },
  },
  destroyed() {
    this.$store.commit('entries/updateData', {});
  },
  methods: {
    isWidgetEmpty(widget) {
      const content = this.$store.getters['sections/getItemByURL'](widget.url);
      if (widget.type === 'text-widget') {
        return !content || content.text_content === null
        || this.$options.filters.striphtml(content.text_content).length === 0;
      }
      if (widget.type === 'image-widget') {
        return this.getImageWidgetThumb(widget).src === null;
      }
      if (widget.type === 'product-widget') {
        return !content || !content.product_images || content.product_images.length < 2;
      }
      return true;
    },
    /** Gets local image widget's png_render or svg content. If imgix, optimize image size */
    getImageWidgetThumb(widget) {
      const content = this.$store.getters['sections/getItemByURL'](widget.url);
      // eslint-disable-next-line
      const imgixRegex = /^http(s?):\/\/[\w\-\.]+\.imgix\.[a-z]{2,6}(\/[\w\-\.]+)*$/i;
      const isImgix = imgixRegex.test(content?.png_render ?? '');

      const result = (content?.png_render && content?.png_render.length
        ? `${isImgix ? `${content?.png_render.split('?')[0]}?fit=clamp&h=64&w=78` : content?.png_render}`
        : content?.svg_image ?? null) ?? null;

      return { isSVG: result?.startsWith('<'), src: result };
    },
    /** Render product widget hero image */
    getProductWidgetThumb(widget) {
      // TODO: Debounce per widget!
      const content = this.$store.getters['sections/getItemByURL'](widget.url);

      // If no elements, return blank
      if (!(content?.product_images?.length >= 2)) return null;
      // get pk from url
      // remove last slash -> split by slash -> get last element
      let pk = widget.url.replace(/\/$/, '').split('/');
      pk = pk[pk.length - 1];
      console.log('Returning url', `${this.api_root}/render_svg/${pk}/`);
      return `${this.api_root}/render_svg/${pk}/`;
    },
    // Not yet used, but might be needed. Let's give it a go first.
    checkAnyTextFocused() {
      this.$nextTick().then(() => this.$refs.widget_components.some((wc) => {
        const isTextWidget = wc.widget.type === 'text-widget';
        const { isFocused } = wc.$refs.component;
        return isTextWidget && isFocused;
      }));
    },
    onDragStart() {
      this.dragging = true;
    },
    onDragEnd() {
      this.dragging = false;
    },
    onDelete(index) {
      this.widgets.splice(index, 1);
      this.$emit('save');
    },
    componentUpdated(component, val) {
      console.log('componentUpdated', JSON.stringify(val, null, 4));
      this.widgets[component.index] = {
        ...this.widgets[component.index],
        ...val,
      };

      console.log(component.block);

      if (component.block.type === 'image-widget') {
        setTimeout(() => this.$forceUpdate(), 0);
      }
    },
    onSavingChange(index) {
      this.saving = this.$refs.widget_components.map((x) => x.saving).reduce((x, y) => x || y, false);
    },
    onAdd(evt) {
      console.log('ON ADD', evt);
      this.widgets.splice(evt.newIndex, 0, {
        type: `${evt.item.dataset.type}-widget`,
        loading: true,
        // dropped: true
      });

      this.$emit('save', ['sections', 'url']);
      // console.log('onAdd',evt.item.dataset.type)
      // self.updateCurr
    },
    getTitle(type) {
      return type === 'image-widget' ? this.$t('outreach.image.title')
        : type === 'text-widget' ? this.$t('outreach.text.title')
          : type === 'product-widget' ? this.$t('outreach.product.title')
            : type === 'intro-widget' ? this.$t('outreach.intro.title')
              : '';
    },
    widgetIsLoading(widget) {
      const sectionIsLoading = this.$store.getters['sections/getStatusByURL'](widget.url) === 'loading';
      return widget && (widget.loading || sectionIsLoading);
    },
    /** Tiny description of the widget, to be shown on dragging */
    getInfo(widget) {
      // find widget
      const data = this.$store.getters['sections/getItemByURL'](widget.url);
      if (!data) {
        return '';
      }
      // Check widget type
      if (widget.type.includes('text')) {
        // text widget
        const content = this.$options.filters.striphtml(data.text_content);
        return content.length ? '' : 'Empty text';
      }
      if (widget.type.includes('product')) {
        return `${data.product_images.length} product${data.product_images.length === 1 ? '' : 's'}`;
      }
      if (widget.type.includes('image')) {
        return data.svg_image === null ? 'Empty image' : '';
      }
      return '';
    },
  },
};
</script>
<style lang="scss"
  scoped>
.draggable-item {
  width: 100%;

  // No widgets, so the 'empty' component is showing
  &--empty {
    transition: margin-bottom .2s ease;
    // 100% empty: Center vertically, shrink to allow drop zone take control of drop radius.
    &:empty {
      width: calc(100% - 190px);
      margin-left: auto;
      margin-right: auto;
      margin-top: 115px;
      margin-bottom: -95px;
      border: 1px solid transparent
    }
    // not empty: A widget creation tool is about to be dropped
    &:not(:empty) {
      width: 100%;
    }
  }
}

.disabled-widget {
  transition: filter .3s ease, opacity .3s ease;
  filter: grayscale(100%);
  opacity: 0.5;
}

.empty-widget {
  background: #FAF9F7;
  height: 148px;
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;

  &__wrapper {
    pointer-events: none;
  }
}

// Hide contents, fix height, fix vertical margin
$vertical-margin: 20px;
// widget preview dimensions
$preview-width: 78px;
$preview-height: 64px;

// hide widgets, show only container
.dragging--ipad {
  ::v-deep &.widget {
    display: none;
  }

  // Hide widget header
  // > div > ::v-deep .widget__title__container.base-heading, ::v-deep .widget__button__delete.base-icon {
  //   display: none;
  // }
  // Hide widget contents, leave only the card
  ::v-deep.bg-white>div {
    display: none;
  }

  margin-bottom: $vertical-margin;
}

.dragging--ipad,
.widget-ghost--ipad {
  ::v-deep .widget {
    display: none;
  }

  height: calc(#{$preview-height} + #{$vertical-margin}) !important;
  border-radius: 5px;
}

$selected-border-width: 4px;
$collapsed-box-margin-y: 12px;

.widget-chosen--ipad {
  &.widget-ghost--ipad {
    border: $selected-border-width solid rgba(0, 123, 255, 0.2);
    // Preview height + 2x vertical margin + 2x colored border width
    height: calc( #{$preview-height} + ( #{$collapsed-box-margin-y} * 2 )
      + ( #{$selected-border-width} * 2 ) ) !important;
    margin-bottom: $vertical-margin;
    ::v-deep .widget {
      background-color: transparent;
    }
  }

  &:not(.widget-ghost--ipad):not(.is-ipad) {
    opacity: 0;
    // background-color: $clementine;
    // height: 40px !important;
    // ::v-deep .page-portrait, ::v-deep .page-landscape {
    //   display: none;
    // }
    // & > * {
    //   opacity: 0;
    // }
  }
}

.widget-collapsed {
  &--ipad {
    background-color: white;
    border-radius: 5px;
    margin-bottom: $vertical-margin;
    padding: $collapsed-box-margin-y 16px;
  }
  &__preview {
    border: 1px solid rgba($gray-light-2, 0.5);
    height: $preview-height;
    width: $preview-width;
    border-radius: 3px;
    background-color: white;
    overflow: hidden;

    &--product {
      height: $preview-height;
      width: $preview-width;
      object-fit: contain;
    }
  }
}
.svg-preview {
  ::v-deep > svg {
    width: 100%;
  }
}

.draggable-dragging {
  .widget-drag--ipad {
    background-color: blue;
    opacity: 0 !important;
    height: 0 !important;
  }
}
.start-heading{
  font-weight: 500;
  font-size: 14px;
  color: #1B1B1B;
}
.start-body{
  font-weight: 300;
  font-size: 12px;
  color: #767676;
}
</style>
